import { useTranslate } from '@tolgee/react';
import { PriceText } from '../PriceText';
import { useFormikContext } from 'formik';
import { LightWeightOrderFormikState, OrderAIFormikState } from './type';
import { useRequest } from 'ahooks';
import { calculatePackagePrice, OnOrder, orderAndPay, pay } from 'services/api/pay';
import dayjs from 'lib/dayjs';
import { Button, message, Modal } from 'antd';
import PayModal, { OrderInfo, OrderInfoLightWeightPkg, OrderInfoTrafficPkg } from '../PayModal';
import useModal from 'hooks/useModal';
import { canPayAirwallex } from '../Package';
import { useAuthorization } from 'components/Authorization';
import { refreshComboInfo } from '../../hooks/useComboInfo';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import wait from 'utils/wait';
import { refreshBotUsageSummary } from 'hooks/business/system/useBot';
import { useMemo } from 'react';
import { find, get } from 'lodash-es';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ReactComponent as DiscountIcon } from '../../assets/erqi/discount.svg';
import Icon from '@ant-design/icons';

type Props = {
  payType: Pay.PayType;
  bank: Pay.PingpongBank;
};

export const OrderResult = (props: Props) => {
  const { payType, bank } = props;
  const { t } = useTranslate();
  const { values } = useFormikContext<OrderAIFormikState>();
  const { data } = useRequest(
    () =>
      calculatePackagePrice({
        orgId: values.orgId!,
        newPackageId: values.newPackageId!,
        handleNum: (values.trafficPackageType === 'INCREASE_THE_MONTHLY_CAP'
          ? values.handleNumCap
          : values.handleNumTraffic)!,
        trafficPackageType: values.trafficPackageType!,
        currency: values.currency!,
      }),
    {
      ready: !!values.trafficPackageType && !!values.currency,
      refreshDeps: [values],
    },
  );

  const { payPrice, afterValidStart, afterValidEnd } = data || {};
  const tipMap: Record<Pay.TrafficPackageType, string> = {
    INCREASE_THE_MONTHLY_CAP: t('aiorder_cap_cal_tip'),
    TRAFFIC_PACKAGE: t('aiorder_tra_cal_tip'),
    ENTERPRISE_TRAFFIC_PACKAGE: '',
  };
  const { visible, show, update: updatePayModal, hide, meta } = useModal<OrderInfo>();
  const { loading, runAsync } = useRequest((info: Parameters<OnOrder>[0]) => orderAndPay(info), {
    manual: true,
    onSuccess: (res, params) => {
      show({
        ...res,
        ...params[0],
        durationNum: values.durationNum,
        validStartStr: afterValidStart,
        validEndStr: afterValidEnd,
      } as OrderInfoTrafficPkg);
    },

    onError: () => {
      message.error(t('market.创建订单支付失败，请稍后再试'));
    },
  });

  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const language = useSystemLanguageValue();

  return (
    <>
      {meta && visible && (
        <PayModal
          visible={visible}
          onCancel={() => hide()}
          orderInfo={meta}
          afterClose={() => {
            updatePayModal(false, undefined);
          }}
          onRefresh={() => {
            const { orderCode, ...others } = meta;
            return pay({
              idempotent: `${Date.now()}`,
              orderCode,
              language,
            })
              .then((res) => {
                updatePayModal(visible, {
                  orderCode,
                  ...others,
                  ...res,
                });
              })
              .catch(() => {
                message.error(t('market.重新加载失败，请稍后再试'));
              });
          }}
          onPayed={() => {
            wait(1000).then(() => {
              refreshComboInfo();
              refreshBotUsageSummary();
            });
          }}
        />
      )}
      <div className="bg-[var(--fett-bg-panel)] mt-[12px] p-[16px] pt-[12px]">
        <div>
          <span className="text-[16px] font-medium">{t('market.总计')}</span>
          <span className="ml-[12px] text-[#ee4e2d] text-[32px] leading-[38px]">
            {payPrice && <PriceText currency={values.currency!} price={payPrice} />}
          </span>
        </div>
        <div className="text-[14px] text-[var(--fett-text-tertiary)]">
          <span>{t('marketing.有效期')}：</span>
          <span>{dayjs(afterValidStart).format('YYYY.MM.DD')}~</span>
          <span>{dayjs(afterValidEnd).format('YYYY.MM.DD')}</span>
          {values.trafficPackageType === 'INCREASE_THE_MONTHLY_CAP' && (
            <span className="ml-[8px]">
              {t('aiorder_cap_rule2', {
                n: values.trafficPkgInfoOrigin?.increaseTheMonthlyCap?.unitPrices?.find(
                  (e) => e.currency === values.currency,
                )?.price,
                m: values.handleNumCap! / 1000,
                k: values.trafficPkgInfoOrigin?.increaseTheMonthlyCap?.durationNum,
              })}
            </span>
          )}
        </div>
        <div className="mt-[4px] text-[var(--fett-surface-warning-text)]">
          {tipMap[values.trafficPackageType!]}
        </div>
      </div>
      <Button
        type="primary"
        size="large"
        style={{
          minWidth: 240,
          marginTop: 16,
        }}
        loading={loading}
        onClick={() => {
          if (payType === 'airwallex' && !canPayAirwallex()) {
            Modal.info({
              title: t('market.当前客户端版本不支持 Airwallex 支付'),
              content: t('market.请重启客户端，升级至最新版本来完成订购'),
            });

            return Promise.resolve();
          }

          runAsync({
            idempotent: `${Date.now()}`,
            accid: payload?.accid!,
            orderAction: 'ORDER',
            payType,
            currency: values.currency!,
            packageId: values.newPackageId!,
            packageType: 4,
            handleNum: (values.trafficPackageType === 'INCREASE_THE_MONTHLY_CAP'
              ? values.handleNumCap
              : values.handleNumTraffic)!,
            trafficPackageType: values.trafficPackageType!,
            discountTotalPrice: `${payPrice}`,
            language,
            bank,
          });
        }}
      >
        {t('market.立即订购')}
      </Button>
    </>
  );
};

export const LightWeightOrderResult = (props: Props) => {
  const { payType, bank } = props;
  const { t } = useTranslate();
  const { values, setFieldValue } = useFormikContext<LightWeightOrderFormikState>();

  const { visible, show, update: updatePayModal, hide, meta } = useModal<OrderInfo>();

  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const language = useSystemLanguageValue();
  const targetC = useMemo(() => {
    const { currency, unitOrderPackageList } = values;
    const target = find(unitOrderPackageList, { isSelected: true });
    return find(target?.currencyAmountList, { currency });
  }, [values]);
  const queryClient = useQueryClient();
  const lang = useSystemLanguageValue();
  const calcEnable = useMemo(() => {
    const { isCustom, currency, customNum } = values;
    const youyu = customNum % 100 === 0;
    return isCustom ? !!currency && youyu : !!currency;
  }, [values]);
  const { currency } = values;
  const originNum = useMemo(() => {
    return values.isCustom
      ? values.customNum
      : find(values.unitOrderPackageList, { isSelected: true })?.orderNumber!;
  }, [values]);

  const { data } = useQuery({
    queryKey: ['calc', originNum, currency] as const,
    enabled: calcEnable,
    queryFn: ({ queryKey }) => {
      return calculatePackagePrice({
        orgId: values.orgId!,
        newPackageId: values.id! as any,
        orderNum: queryKey[1],
        currency: queryKey[2]!,
      }).then((res) => {
        if (res.totalOrderNum) {
          setFieldValue('totalOrderNum', res.totalOrderNum);
        }
        return res;
      });
    },
  });

  const startDate = calcEnable ? data?.afterValidStart : values.startDate;
  const endDate = calcEnable ? data?.afterValidEnd : values.endDate;
  const payPrice = calcEnable ? data?.payPrice : targetC?.price;
  const originPrice = calcEnable ? data?.originPrice : targetC?.originPrice;
  const totalOrderNum = calcEnable ? data?.totalOrderNum : originNum;

  const { loading, runAsync } = useRequest((info: Parameters<OnOrder>[0]) => orderAndPay(info), {
    manual: true,
    onSuccess: (res, params) => {
      show({
        ...res,
        ...params[0],
        orderNumber: totalOrderNum,
      } as OrderInfoLightWeightPkg);
    },

    onError: () => {
      message.error(t('market.创建订单支付失败，请稍后再试'));
    },
  });

  const discount = get(data, 'discount', 0);

  return (
    <>
      {meta && visible && (
        <PayModal
          visible={visible}
          onCancel={() => hide()}
          orderInfo={meta}
          afterClose={() => {
            updatePayModal(false, undefined);
          }}
          onRefresh={() => {
            const { orderCode, ...others } = meta;
            return pay({
              idempotent: `${Date.now()}`,
              orderCode,
              language,
            })
              .then((res) => {
                updatePayModal(visible, {
                  orderCode,
                  ...others,
                  ...res,
                });
              })
              .catch(() => {
                message.error(t('market.重新加载失败，请稍后再试'));
              });
          }}
          onPayed={() => {
            wait(1000).then(() => {
              queryClient.invalidateQueries(['lightWeightPackageInfo']);
            });
          }}
        />
      )}
      <div className="bg-[var(--fett-bg-panel)] mt-[12px] p-[16px] pt-[12px]">
        <div>
          <span className="text-[16px] font-medium">{t('market.总计')}</span>
          <span className="ml-[12px] text-[#ee4e2d] text-[32px] leading-[38px]">
            {payPrice && <PriceText currency={values.currency!} price={payPrice} />}
          </span>
          {discount > 0 && (
            <span>
              <span className="text-[var(--fett-text-tertiary)] line-through">
                <PriceText currency={values.currency!} price={originPrice!} />
              </span>
              <span className="bg-[#ffd6d6] inline-flex items-center text-[12px] h-[24px] px-[6px] rounded-[8px] ml-[6px] rounded-bl-[0px]">
                <Icon style={{ marginRight: 4 }} component={DiscountIcon} />
                {t('{{x}}折优惠', {
                  x:
                    lang === 'zh-CN'
                      ? Number((discount * 10).toFixed(1))
                      : `${Number(((1 - discount) * 100).toFixed(2))}`,
                })}
              </span>
            </span>
          )}
        </div>
        <div className="text-[14px] text-[var(--fett-text-tertiary)]">
          <span>{t('marketing.有效期')}：</span>
          <span>{dayjs(startDate).format('YYYY.MM.DD')}~</span>
          <span>{dayjs(endDate).format('YYYY.MM.DD')}</span>
        </div>
      </div>
      <Button
        type="primary"
        size="large"
        style={{
          minWidth: 240,
          marginTop: 16,
        }}
        loading={loading}
        onClick={() => {
          if (payType === 'airwallex' && !canPayAirwallex()) {
            Modal.info({
              title: t('market.当前客户端版本不支持 Airwallex 支付'),
              content: t('market.请重启客户端，升级至最新版本来完成订购'),
            });

            return Promise.resolve();
          }

          runAsync({
            idempotent: `${Date.now()}`,
            accid: payload?.accid!,
            orderAction: 'ORDER',
            packageId: values.id!,
            payType,
            packageType: 5,
            currency: values.currency!,
            orderNumber: originNum,
            discountTotalPrice: `${payPrice}`,
            language,
            bank,
          });
        }}
      >
        {t('market.立即订购')}
      </Button>
    </>
  );
};
